<template lang="html">
  <div v-show="isFilterVisible">
    <transition name="fade">
      <aside
        v-tracking-context="{ content_element: 'filter_settings' }"
        class="filter-settings"
      >
        <div class="filter-settings__headline flex-container align-justify">
          {{ 'client.filter_settings.headline' | t }}
          <a
            v-click-track="{ action_type: 'filter_close' }"
            class="filter-settings__close"
            @click.prevent="filterToggle"
          >
            <svg-icon icon-name="close" />
          </a>
        </div>
        <div class="filter-settings__contents">
          <div
            v-for="(category, idx) in categories"
            :key="category.key"
            v-tracking-context="{
              event_element: 'category',
              event_element_pos: idx + 1
            }"
            class="filter-settings__category"
            :class="{
              'filter-settings__category--active':
                visibleCategory === category.key
            }"
          >
            <div
              v-click-track="{
                action_type: 'category_toggle',
                target: `#${category.key}`
              }"
              class="filter-settings__category-label"
              :class="{
                'filter-settings__category-label--applied':
                  partiallyCheckedCollections.has(`${category.id}`)
              }"
              @click="toggleCategory(category.key)"
            >
              <div class="filter-settings__label">
                {{ category.label }}
              </div>
              <svg-icon
                icon-name="chevron-left"
                class-name="filter-settings__chevron"
              />
            </div>
            <div class="filter-settings__category-body">
              <div
                v-for="group in sortByUiPosition(
                  category.grouped_category_tags
                )"
                :key="group.key"
                class="filter-settings__group"
                :class="{
                  'filter-settings__group--active':
                    visibleGroup === group.key || !group.key
                }"
              >
                <div
                  v-if="group.key"
                  v-click-track="{
                    action_type: 'category_group_toggle',
                    target: `#${group.key}`
                  }"
                  class="filter-settings__group-label"
                  :class="{
                    'filter-settings__group-label--applied':
                      partiallyCheckedCollections.has(
                        `${category.id}-${group.id}`
                      )
                  }"
                  @click="toggleGroup(group.key)"
                >
                  <div class="filter-settings__label">
                    {{ group.label }}
                  </div>
                  <svg-icon
                    icon-name="chevron-left"
                    class-name="filter-settings__chevron"
                  />
                </div>
                <div class="filter-settings__group-body">
                  <div class="filter-settings__tag">
                    <input
                      :id="`filter-${category.id}-${group.id}-all`"
                      v-click-track="{
                        action_type: 'category_tag_toggle',
                        target: `#${group.key}-all`
                      }"
                      class="custom-input custom-input--checkbox"
                      type="checkbox"
                      :checked="checkedGroups[`${category.id}-${group.id}`]"
                      @click="toggleGroupTags(category, group)"
                    />
                    <label
                      class="filter-settings__tag-label"
                      :for="`filter-${category.id}-${group.id}-all`"
                      >{{ 'client.filter_settings.select_all' | t }}</label
                    >
                  </div>
                  <div
                    v-for="tag in sortByUiPosition(group.group_tags)"
                    :key="tag.key"
                    class="filter-settings__tag"
                  >
                    <input
                      :id="`filter-${category.id}-${group.id}-${tag.id}`"
                      v-click-track="{
                        action_type: 'category_tag_toggle',
                        target: `#${tag.key}`
                      }"
                      class="custom-input custom-input--checkbox"
                      type="checkbox"
                      :name="tag.key"
                      :checked="checkedCategoryTagsMap[tag.key]"
                      @click="debouncedToggleTag(tag.key)"
                    />
                    <label
                      class="filter-settings__tag-label"
                      :for="`filter-${category.id}-${group.id}-${tag.id}`"
                    >
                      <a
                        :href="seo_link(tag)"
                        :title="tag.label"
                        @click.prevent="debouncedToggleTag(tag.key)"
                      >
                        {{ tag.label }}
                      </a>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="filter-settings__button">
          <a
            v-click-track="{ action_type: 'results_show' }"
            class="button button--primary"
            :class="{ disabled: resultsLoading }"
            @click.prevent="showResults"
          >
            <span v-if="!resultsLoading">{{
              'client.filter_settings.show_results' | t({ count: resultsCount })
            }}</span>
            <span v-else>...</span>
          </a>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import _debounce from 'lodash-es/debounce'
import flatMap from 'lodash-es/flatMap'
import flatMapDeep from 'lodash-es/flatMapDeep'
import some from 'lodash-es/some'
import fromPairs from 'lodash-es/fromPairs'
import every from 'lodash-es/every'
import orderBy from 'lodash-es/orderBy'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

export default {
  data: () => ({
    visibleCategory: null,
    visibleGroup: null
  }),
  computed: {
    ...mapGetters(['checkedCategoryTagsMap', 'isFilterVisible']),
    ...mapState([
      'categories',
      'searchProfile',
      'resultsCount',
      'resultsLoading',
      'seoLinks'
    ]),
    checkedGroups() {
      return fromPairs(
        flatMap(this.categories, category => {
          return category.grouped_category_tags
            .filter(group =>
              every(
                group.group_tags,
                tag => this.checkedCategoryTagsMap[tag.key]
              )
            )
            .map(group => [`${category.id}-${group.id}`, true])
        })
      )
    },
    partiallyCheckedCollections() {
      return new Set(
        flatMapDeep(this.categories, category => {
          return category.grouped_category_tags
            .filter(group =>
              some(
                group.group_tags,
                tag => this.checkedCategoryTagsMap[tag.key]
              )
            )
            .map(group => [`${category.id}`, `${category.id}-${group.id}`])
        })
      )
    }
  },
  methods: {
    ...mapActions(['filterToggle', 'resultsSelectFirst']),
    ...mapMutations([
      'searchProfileRemoveTag',
      'searchProfileAddTags',
      'searchProfileRemoveTags'
    ]),
    showResults() {
      this.filterToggle()
      this.resultsSelectFirst()
    },
    debouncedToggleTag: _debounce(
      function (tagKey) {
        this.toggleTag(tagKey)
      },
      300,
      { leading: false, trailing: true }
    ),
    toggleTag(tagKey) {
      if (this.checkedCategoryTagsMap[tagKey]) {
        this.searchProfileRemoveTag(tagKey)
      } else {
        this.searchProfileAddTags({ tagKeys: [tagKey] })
      }
    },
    toggleCategory(key) {
      if (this.visibleCategory === key) {
        this.visibleCategory = null
      } else {
        this.visibleCategory = key
      }
    },
    toggleGroup(key) {
      if (this.visibleGroup === key) {
        this.visibleGroup = null
      } else {
        this.visibleGroup = key
      }
    },
    toggleGroupTags(category, group) {
      if (this.checkedGroups[`${category.id}-${group.id}`]) {
        this.searchProfileRemoveTags(group.group_tags.map(tag => tag.key))
      } else {
        this.searchProfileAddTags({
          tagKeys: group.group_tags.map(tag => tag.key)
        })
      }
    },
    seo_link(tag) {
      return this.seoLinks ? this.seoLinks[tag.id] : null
    },
    sortByUiPosition(items) {
      return orderBy(items, [item => parseInt(item.ui_position)], ['asc'])
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-settings {
  transition-duration: 0.3s;
}

.filter-enter,
.filter-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
